import React, { useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useUserStore } from '../store/userStore';
import { AuraLogo } from './Brand';

const NAV = [
  { to: '/app', label: 'Home', icon: 'fa-house', end: true, badge: true },
  { to: '/app/track', label: 'Track', icon: 'fa-pen-to-square' },
  { to: '/app/insights', label: 'Insights', icon: 'fa-wand-magic-sparkles' },
  { to: '/app/book', label: 'Book', icon: 'fa-calendar-check' },
  { to: '/app/profile', label: 'Profile', icon: 'fa-user' },
];

export function AppLayout() {
  const { user, logout, unreadCount, refreshUnread } = useUserStore();
  const navigate = useNavigate();

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const doLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--aura-bg)' }}>
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-[var(--aura-line)] bg-white/60 backdrop-blur-xl p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-8">
          <AuraLogo size={40} />
          <div>
            <div className="font-display text-lg leading-none">Auralis</div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--aura-ink-soft)] mt-1">
              Health
            </div>
          </div>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-base ${
                  isActive
                    ? 'bg-[var(--aura-purple)] text-white shadow-md'
                    : 'text-[var(--aura-ink-soft)] hover:bg-[var(--aura-purple)]/8 hover:text-[var(--aura-purple)]'
                }`
              }
            >
              <i className={`fa-solid ${n.icon} w-5 text-center`} />
              {n.label}
              {n.badge && unreadCount > 0 && (
                <span data-testid="nav-unread-badge" className="ml-auto grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-[#EC4899] text-white text-[11px] font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-4 border-t border-[var(--aura-line)]">
          <div className="flex items-center gap-3 mb-3">
            {user?.profile_image ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`} alt="You" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="grid place-items-center w-9 h-9 rounded-full bg-[var(--aura-purple)]/12 text-[var(--aura-purple)] font-semibold">
                {(user?.name || 'U').charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-[var(--aura-ink-soft)] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full text-sm text-[var(--aura-ink-soft)] hover:text-[var(--aura-purple)] flex items-center gap-2 px-3 py-2"
          >
            <i className="fa-solid fa-arrow-right-from-bracket" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (mobile) */}
        <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-xl border-b border-[var(--aura-line)]">
          <div className="flex items-center gap-2">
            <AuraLogo size={34} />
            <span className="font-display text-lg">Auralis</span>
          </div>
          <button onClick={doLogout} className="text-[var(--aura-ink-soft)] px-2" data-testid="logout-btn-mobile">
            <i className="fa-solid fa-arrow-right-from-bracket" />
          </button>
        </header>

        <main className="flex-1 pb-24 md:pb-8">
          <Outlet />
        </main>

        {/* Bottom tabs (mobile) */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-[var(--aura-line)] flex justify-around px-2 py-1.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] font-medium ${
                  isActive ? 'text-[var(--aura-purple)]' : 'text-[var(--aura-ink-soft)]'
                }`
              }
            >
              <span className="relative">
                <i className={`fa-solid ${n.icon} text-base`} />
                {n.badge && unreadCount > 0 && (
                  <span data-testid="nav-unread-badge-mobile" className="absolute -top-1.5 -right-2 grid place-items-center min-w-4 h-4 px-1 rounded-full bg-[#EC4899] text-white text-[9px] font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, backTo, right }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-start gap-3 mb-6">
      {backTo !== undefined && (
        <button
          onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
          data-testid="page-back-btn"
          className="mt-1 grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)] text-[var(--aura-ink-soft)] hover:text-[var(--aura-purple)] transition-base"
        >
          <i className="fa-solid fa-arrow-left" />
        </button>
      )}
      <div className="flex-1">
        <h1 className="font-display text-3xl sm:text-4xl">{title}</h1>
        {subtitle && <p className="text-[var(--aura-ink-soft)] mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}
